/**
 * ProjectSetupCard — once-per-project "confirm global project setup" card.
 *
 * Harness-emitted by the Director on the first user turn of a new project's
 * default canvas (UserQuestion with `metadata.render_as === 'project_setup_card'`).
 * Edits stay pending until Confirm durably saves them through useProjectConfig.
 * Untouched fields track live Global Config updates. Style opens the shared
 * selector; advanced settings remain available through Open global config.
 *
 * Feature tag: `project-setup-card` (`nad trace --feature project-setup-card`).
 */
import { memo, useCallback, useState, useRef, useEffect, useId, type KeyboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Triangle } from 'lucide-react'
import { useProjectConfig, useUpdateProjectConfig } from '../../api/hooks'
import type { ProjectConfig, UpdateProjectConfigRequest, AspectRatio } from '../../api/types'
import { useVideoModels } from '../../hooks/useVideoModels'
import { useImageModels } from '../../hooks/useImageModels'
import { getImageModelLabel } from '../../constants/modelAliases'
import { FlyoutPortal } from '../canvas-v2/nodes/shared/FlyoutPortal'
import { StyleLibraryModal } from '../canvas-v2/nodes/shared/StyleLibraryModal'
import { getByomAspectRatios, isByomModel } from '../canvas-v2/nodes/shared/videoConstraints'
import { useStyleLabel } from '../../hooks/useStyleLabel'
import { createNadDebug } from '../../utils/debugLog'
import { buildContentLanguageOptions, CONTENT_LANGUAGE_AUTO, CONTENT_LANGUAGE_CODES } from '../../i18n/contentLanguages'
import { normalizeVideoPreferences, videoPreferenceLabel, videoPreferenceAspectRatios } from '../canvas-v2/videoFamilyPreference'
import { resolveVideoModelRow } from '../canvas-v2/videoModelRow'
import {
  openProjectSettings,
  type ProjectSettingsOpenSource,
} from '../canvas-v2/projectSettingsEvents'
import type { AutoConfirmCostSettings } from './autoConfirmCost'

/** Max model names shown before collapsing the rest into "+N". */
const MAX_VISIBLE_MODEL_NAMES = 2

export interface ProjectConfigSummary {
  config: ProjectConfig | undefined
  aspectRatio: string | null
  styleLabel: string | null
  /** Display text for the video row; `null` only while nothing has loaded. */
  videoModels: string | null
  imageModel: string | null
}

/** Live, display-ready Global Config values shared by the chat config cards. */
export function useProjectConfigSummary(projectId: string, canvasId: string): ProjectConfigSummary {
  const { t } = useTranslation('chat')
  const { data: config } = useProjectConfig(projectId, canvasId)
  const { byom, defaultModel } = useVideoModels()
  const { byom: imageByom } = useImageModels()
  const { label: styleLabel } = useStyleLabel(config?.style_id)

  const row = resolveVideoModelRow(
    config?.preferred_video_models ?? [],
    config?.effective_default_video_model,
    defaultModel,
    config?.effective_default_video_model_runnable,
  )
  let videoModels: string | null = null
  if (config && row.kind === 'default') {
    videoModels = t(
      row.runnable ? 'projectSetupCard.defaultSuffix' : 'projectSetupCard.defaultNotRunnable',
      { name: videoPreferenceLabel(row.id, byom) },
    )
  } else if (config && row.kind === 'preferred') {
    const ids = normalizeVideoPreferences(row.ids)
    const names = ids
      .slice(0, MAX_VISIBLE_MODEL_NAMES)
      .map((id) => videoPreferenceLabel(id, byom))
    const hidden = ids.length - names.length
    videoModels = hidden > 0
      ? `${names.join(', ')} ${t('projectSetupCard.moreModels', { count: hidden })}`
      : names.join(', ')
  }

  return {
    config,
    aspectRatio: config?.aspect_ratio ?? null,
    styleLabel: config ? styleLabel : null,
    videoModels,
    imageModel: !config ? null : !config.preferred_image_model || config.preferred_image_model === 'auto'
      ? t('projectSetupCard.autoImageModel')
      : imageByom.find((model) => model.id === config.preferred_image_model)?.key_name ?? getImageModelLabel(config.preferred_image_model),
  }
}

/** Card-body click → open Global Config, keyboard-accessible. */
export function useOpenSettingsHandlers(source: ProjectSettingsOpenSource) {
  const open = useCallback(() => {
    openProjectSettings(source)
  }, [source])
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openProjectSettings(source)
    }
  }, [source])
  return { onClick: open, onKeyDown }
}

const editLog = createNadDebug('dw:editable-project-setup')
const PROJECT_ASPECT_RATIOS: AspectRatio[] = ['16:9', '9:16']
const CHIP = 'relative inline-flex min-w-0 max-w-full items-center rounded-full border border-primary/40 bg-input-bg text-text-secondary hover:bg-surface-2 hover:border-primary/70 focus-within:ring-2 focus-within:ring-primary/50 focus-within:ring-offset-2 focus-within:ring-offset-surface-1'

function SetupRow({ testId, label, children }: { testId: string; label: string; children: ReactNode }) {
  return <div className="flex items-center justify-between gap-2 py-2" data-testid={testId}>
    <dt className="shrink-0 text-xs text-text-secondary">{label}</dt>
    <dd className="min-w-0 max-w-full">{children}</dd>
  </div>
}

interface SetupOption { value: string; label: string; disabled?: boolean }

/** DW's anchored flyout escapes the chat scroller; the preview stays within its row. */
function SetupChip({ label, value, options, disabled, onChange }: {
  label: string; value: string; options: SetupOption[]; disabled: boolean; onChange: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const previewId = useId()
  const preview = options.find(option => option.value === value)?.label ?? value
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])
  const focusPending = useRef(false)
  useEffect(() => { focusPending.current = open }, [open])
  function focusMenu() {
    if (focusPending.current) {
      const menu = menuRef.current
      const selected = menu?.querySelector<HTMLButtonElement>('[aria-selected="true"]:not(:disabled)')
      const target = selected ?? menu?.querySelector<HTMLButtonElement>('button:not(:disabled)')
      target?.focus()
      focusPending.current = false
    }
  }
  function closeAndFocus() {
    setOpen(false)
    triggerRef.current?.focus()
  }
  function navigate(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Tab') { setOpen(false); triggerRef.current?.focus(); return }
    const buttons = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])
    const index = buttons.findIndex(button => button === document.activeElement)
    let next: number
    if (event.key === 'ArrowDown') next = (index + 1) % buttons.length
    else if (event.key === 'ArrowUp') next = (index - 1 + buttons.length) % buttons.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = buttons.length - 1
    else return
    event.preventDefault()
    buttons[next]?.focus()
  }
  return <>
    <button ref={triggerRef} type="button" aria-label={label} aria-describedby={previewId} title={preview} disabled={disabled}
      aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? menuId : undefined}
      onClick={() => setOpen(current => !current)}
      onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true) }
      }}
      className={`${CHIP} h-7 gap-2 px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50`}>
      <span id={previewId} className="min-w-0 truncate">{preview}</span>
      <Triangle aria-hidden="true" className="h-2 w-2 shrink-0 rotate-180 fill-current" />
    </button>
    <FlyoutPortal triggerRef={triggerRef} isOpen={open && !disabled} onClose={() => setOpen(false)} onEscape={closeAndFocus}
      onPositionChange={focusMenu} closeOnCanvasViewportChange={false} preferredPlacement="below" minWidth={200} zIndex={1200}
      className="max-w-sm" surfaceClassName="max-h-64 overflow-y-auto rounded-lg border border-border bg-surface-1 py-1 shadow-xl">
      <div ref={menuRef} id={menuId} role="listbox" aria-label={label} onKeyDown={navigate}>
        {options.map(option => <button key={option.value} type="button" role="option" tabIndex={-1}
          aria-selected={option.value === value} disabled={option.disabled}
          onClick={() => { onChange(option.value); closeAndFocus() }}
          className="block w-full break-words px-3 py-2 text-left text-xs text-text-secondary hover:bg-surface-2 hover:text-foreground focus:bg-surface-2 focus:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50 aria-selected:bg-surface-2 aria-selected:text-foreground disabled:cursor-not-allowed disabled:opacity-50">
          {option.label}
        </button>)}
      </div>
    </FlyoutPortal>
  </>
}

export interface ProjectSetupCardProps {
  projectId: string
  canvasId: string
  autoConfirmCostSettings: AutoConfirmCostSettings
  onConfirm: () => void
  disabled?: boolean
}

export const ProjectSetupCard = memo(function ProjectSetupCard({ projectId, canvasId, ...props }: ProjectSetupCardProps) {
  // Remount the pending form when navigating between projects/canvases.
  return <ProjectSetupForm key={`${projectId}/${canvasId}`} projectId={projectId} canvasId={canvasId} {...props} />
})

function ProjectSetupForm({ projectId, canvasId, autoConfirmCostSettings, onConfirm, disabled = false }: ProjectSetupCardProps) {
  const { t } = useTranslation('chat')
  const summary = useProjectConfigSummary(projectId, canvasId)
  const { mutateAsync: updateConfig } = useUpdateProjectConfig()
  const videoCatalog = useVideoModels()
  const imageCatalog = useImageModels()
  const [draft, setDraft] = useState<UpdateProjectConfigRequest>({})
  const styleDescriptionId = useId()
  const [styleOpen, setStyleOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const saveInFlight = useRef(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const config = summary.config
  const styleId = draft.style_id ?? config?.style_id
  const { label: styleLabel } = useStyleLabel(styleId)
  const aspect = draft.aspect_ratio ?? config?.aspect_ratio
  const contentLanguage = draft.content_language ?? config?.content_language ?? CONTENT_LANGUAGE_AUTO
  const preferred = normalizeVideoPreferences(draft.preferred_video_models ?? config?.preferred_video_models ?? [])
  const videoValue = draft.preferred_video_models === undefined ? '__current' : preferred[0] ?? ''
  const imageValue = draft.preferred_image_model ?? config?.preferred_image_model ?? 'auto'
  const duration = draft.target_duration_s !== undefined ? draft.target_duration_s : config?.target_duration_s ?? null
  const videoIds = normalizeVideoPreferences([...videoCatalog.native, ...videoCatalog.gateway, ...videoCatalog.byom.filter(m => m.enabled).map(m => m.id)])
  const imageIds = [...new Set([...imageCatalog.native, ...imageCatalog.byom.filter(m => m.enabled).map(m => m.id)])]
  const selectedVideo = preferred[0] ?? (draft.preferred_video_models ? videoCatalog.defaultModel : config?.effective_default_video_model ?? videoCatalog.defaultModel)
  const byom = videoCatalog.byom.find(m => m.id === selectedVideo)
  const ratios = selectedVideo ? isByomModel(selectedVideo)
    ? getByomAspectRatios(byom?.capabilities ?? null)
    : videoPreferenceAspectRatios(selectedVideo, videoCatalog.native) : []
  // Adaptive output follows the reference image, which can use either project orientation.
  const allowedRatios: AspectRatio[] = PROJECT_ASPECT_RATIOS.filter(r => ratios.includes(r) || ratios.includes('adaptive') || ratios.includes('Auto'))
  const incompatibleAspect = !!aspect && !!selectedVideo && ratios.length > 0 && !allowedRatios.includes(aspect)
  const unavailableVideo = videoCatalog.isReady && (preferred.some(id => !videoIds.includes(id)) || (preferred.length === 0 && draft.preferred_video_models === undefined && config?.effective_default_video_model_runnable === false))
  const unavailableImage = !imageCatalog.isLoading && !imageCatalog.error && imageValue !== 'auto' && !imageIds.includes(imageValue)
  const invalidDuration = duration !== null && (!Number.isInteger(duration) || duration < 1 || duration > 3600)
  const catalogError = videoCatalog.error || imageCatalog.error
  // A catalog outage must not block accepting stored settings. Edited model
  // fields (and aspect compatibility) still require their catalog to validate.
  const videoNeedsCatalog = draft.preferred_video_models !== undefined || draft.aspect_ratio !== undefined
  const imageNeedsCatalog = draft.preferred_image_model !== undefined
  const editedCatalogUnavailable = (videoNeedsCatalog && (!!videoCatalog.error || videoCatalog.isLoading))
    || (imageNeedsCatalog && (!!imageCatalog.error || imageCatalog.isLoading))
  const invalid = incompatibleAspect || unavailableVideo || unavailableImage || invalidDuration || editedCatalogUnavailable
  const locked = disabled || saving || !config
  const loading = t('projectSetupCard.loading')
  const videoLabel = (id: string) => videoPreferenceLabel(id, videoCatalog.byom)
  const durations = [...new Set([15, 30, 60, 90, 120, 180, 300, ...(duration === null ? [] : [duration])])].sort((a, b) => a - b)

  function change<K extends keyof UpdateProjectConfigRequest>(field: K, value: UpdateProjectConfigRequest[K]) {
    setDraft(current => ({ ...current, [field]: value }))
    setSaveError(null)
    editLog({ event: 'draft_changed', project_id: projectId, canvas_id: canvasId, occurred_at: new Date().toISOString(), field, value: JSON.stringify(value),
      ...(field === 'content_language' ? { previous_content_language: contentLanguage === CONTENT_LANGUAGE_AUTO ? null : contentLanguage, content_language: value } : {}),
    })
  }

  async function handleConfirm() {
    if (locked || invalid || saveInFlight.current) return
    saveInFlight.current = true
    setSaving(true)
    setSaveError(null)
    const traceId = crypto.randomUUID()
    editLog({ event: 'save_started', project_id: projectId, canvas_id: canvasId, trace_id: traceId, occurred_at: new Date().toISOString(), changed_fields: Object.keys(draft).join(',') })
    try {
      if (Object.keys(draft).length > 0) await updateConfig({ projectId, canvasId, data: draft })
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setSaveError(message)
      setSaving(false)
      saveInFlight.current = false
      editLog({ event: 'save_failed', project_id: projectId, canvas_id: canvasId, trace_id: traceId, occurred_at: new Date().toISOString(), error: message, director_continued: false })
      return
    }
    editLog({ event: 'saved_before_confirm', project_id: projectId, canvas_id: canvasId, trace_id: traceId, occurred_at: new Date().toISOString(), aspect_ratio: aspect, content_language: contentLanguage, target_duration_s: duration, style_id: styleId, preferred_video_models: preferred.join(','), preferred_image_model: imageValue })
    onConfirm()
    editLog({ event: 'confirm_dispatched', project_id: projectId, canvas_id: canvasId, trace_id: traceId, occurred_at: new Date().toISOString(), content_language: contentLanguage })
    setSaving(false)
    saveInFlight.current = false
  }

  return <div className="rounded-lg border border-border bg-surface-1 p-4" data-testid="project-setup-card">
    <h3 className="mb-0.5 text-sm font-medium text-text-primary">{t('projectSetupCard.title')}</h3>
    <p className="mb-2 text-xs text-text-muted">{t('projectSetupCard.editHint')}</p>
    <dl className="mb-3 divide-y divide-border-subtle">
      <SetupRow testId="project-setup-row-aspect" label={t('projectSetupCard.aspectRatio')}>
        <SetupChip label={t('projectSetupCard.aspectRatio')} value={aspect ?? ''} disabled={locked} onChange={value => {
          if (value !== '16:9' && value !== '9:16') throw new Error(`Invalid project aspect ratio: ${value}`)
          change('aspect_ratio', value)
        }} options={[
          ...(!aspect ? [{ value: '', label: loading }] : []),
          ...PROJECT_ASPECT_RATIOS.map(r => ({ value: r, label: r, disabled: ratios.length > 0 && !allowedRatios.includes(r) })),
        ]} />
      </SetupRow>
      <SetupRow testId="project-setup-row-content-language" label={t('canvas:projectConfigGear.contentLanguage')}>
        <SetupChip label={t('canvas:projectConfigGear.contentLanguage')} value={contentLanguage} disabled={locked} onChange={value => {
          if (value !== CONTENT_LANGUAGE_AUTO && !CONTENT_LANGUAGE_CODES.includes(value)) throw new Error(`Invalid project content language: ${value}`)
          change('content_language', value)
        }} options={buildContentLanguageOptions(t)} />
      </SetupRow>
      <SetupRow testId="project-setup-row-style" label={t('projectSetupCard.style')}>
        <button type="button" disabled={locked} aria-label={t('projectSetupCard.style')} aria-describedby={styleDescriptionId} className={`${CHIP} h-7 px-3 text-xs font-medium disabled:opacity-50`} onClick={() => {
          editLog({ event: 'style_selector_opened', project_id: projectId, canvas_id: canvasId, style_id: styleId })
          setStyleOpen(true)
        }}><span id={styleDescriptionId} className="truncate">{config ? styleLabel : loading}</span></button>
      </SetupRow>
      <SetupRow testId="project-setup-row-image" label={t('projectSetupCard.preferredImageModel')}>
        <SetupChip label={t('projectSetupCard.preferredImageModel')} value={imageValue} disabled={locked || imageCatalog.isLoading || !!imageCatalog.error} onChange={value => change('preferred_image_model', value)} options={[
          { value: 'auto', label: t('projectSetupCard.autoImageModel') },
          ...(unavailableImage ? [{ value: imageValue, label: `${getImageModelLabel(imageValue)} — ${t('projectSetupCard.unavailable')}`, disabled: true }] : []),
          ...imageIds.map(id => ({ value: id, label: imageCatalog.byom.find(m => m.id === id)?.key_name ?? getImageModelLabel(id) })),
        ]} />
      </SetupRow>
      <SetupRow testId="project-setup-row-video" label={t('projectSetupCard.preferredVideoModel')}>
        <SetupChip label={t('projectSetupCard.preferredVideoModel')} value={videoValue} disabled={locked || videoCatalog.isLoading || !!videoCatalog.error} onChange={value => change('preferred_video_models', value ? [value] : [])} options={[
          ...(videoValue === '__current' ? [{ value: '__current', label: summary.videoModels ?? loading, disabled: true }] : []),
          { value: '', label: videoCatalog.defaultModel ? t('projectSetupCard.defaultSuffix', { name: videoLabel(videoCatalog.defaultModel) }) : t('projectSetupCard.autoImageModel') },
          ...videoIds.map(id => ({ value: id, label: videoLabel(id) })),
        ]} />
      </SetupRow>
      <SetupRow testId="project-setup-row-duration" label={t('projectSetupCard.targetDuration')}>
        <SetupChip label={t('projectSetupCard.targetDuration')} value={duration?.toString() ?? ''} disabled={locked} onChange={value => change('target_duration_s', value ? Number(value) : null)} options={[
          { value: '', label: t('projectSetupCard.noTarget') },
          ...durations.map(seconds => ({ value: String(seconds), label: t('projectSetupCard.durationSeconds', { count: seconds }) })),
        ]} />
      </SetupRow>
    </dl>
    <p className="mb-3 text-xxs text-text-muted" data-testid="project-setup-row-cost">{t('projectSetupCard.costConfirmation')}: {t('projectSetupCard.autoConfirmSummary', {
      image: t(autoConfirmCostSettings.image ? 'projectSetupCard.autoConfirmOn' : 'projectSetupCard.autoConfirmOff'),
      video: t(autoConfirmCostSettings.video ? 'projectSetupCard.autoConfirmOn' : 'projectSetupCard.autoConfirmOff'),
    })}</p>
    {incompatibleAspect && <p role="alert" className="mb-3 text-xs text-destructive">{t('projectSetupCard.incompatibleAspect', { model: selectedVideo ? videoLabel(selectedVideo) : '', ratio: aspect })}</p>}
    {(unavailableVideo || unavailableImage) && <p role="alert" className="mb-3 text-xs text-destructive">{t('projectSetupCard.chooseAvailableModel')}</p>}
    {invalidDuration && <p role="alert" className="mb-3 text-xs text-destructive">{t('projectSetupCard.invalidDuration')}</p>}
    {(saveError || catalogError) && <p role="alert" className="mb-3 text-xs text-destructive">{saveError || catalogError}</p>}
    <div className="flex flex-wrap gap-2">
      <button type="button" disabled={locked || invalid || videoCatalog.isLoading || imageCatalog.isLoading} onClick={() => void handleConfirm()} data-testid="project-setup-confirm"
        className="h-7 rounded-lg bg-chrome px-3 text-xs font-medium text-chrome-foreground hover:bg-chrome/90 focus-visible:ring-2 focus-visible:ring-chrome/50 disabled:cursor-not-allowed disabled:opacity-50">
        {saving ? t('projectSetupCard.saving') : t('projectSetupCard.confirm')}
      </button>
      <button type="button" disabled={saving} onClick={() => openProjectSettings('project_setup_card')} data-testid="project-setup-open-config"
        className="h-7 rounded-lg border border-input-border bg-surface-1 px-3 text-xs text-foreground hover:border-input-border-hover hover:bg-surface-3 focus-visible:ring-2 focus-visible:ring-primary/50">
        {t('projectSetupCard.openGlobalConfig')}
      </button>
    </div>
    {styleOpen && <StyleLibraryModal isOpen currentStyleId={styleId ?? ''} onClose={() => setStyleOpen(false)} onSelect={id => { change('style_id', id); setStyleOpen(false) }} />}
  </div>
}

/** Read-only transcript entry for an answered config card. */
export const AnsweredConfigCard = memo(function AnsweredConfigCard({
  testId,
  label,
  detail,
}: {
  testId: string
  label: string
  detail?: string
}) {
  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-border-subtle bg-surface-1 px-3 py-2 text-xs font-medium text-text-secondary"
      data-testid={testId}
    >
      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted" aria-hidden="true" />
      <span className="min-w-0">
        <span className="block text-text-primary">{label}</span>
        {detail && <span className="mt-0.5 block text-text-muted">{detail}</span>}
      </span>
    </div>
  )
})
